import UIErrorMessage from '$shared/constants/UIErrorMessage';
import { countryData } from '$provider/lib/datas/CountryData';
import type { IProfileMergeRecordAddressModel } from '$provider/lib/apis/interfaces/IProfileMergeRecordAddressModel';
import type { AddressInputFormDataModel } from '$shared/models/forms/AddressInputFormDataModel';

export type RequiredAddressField = 'addressLine1' | 'city' | 'state' | 'zipCode' | 'countryId';

export class AddressValidator {
  /**
   * Get required address fields based on country
   * US addresses require: street, city, state, ZIP, country
   * International addresses require: street, city, country
   * @param countryId - Country identifier or null/undefined for US default
   * @returns Array of required field names
   */
  static requiredFields(countryId: string | null | undefined): RequiredAddressField[] {
    const isUSACountry = countryData.isUSA(countryId || '');
    if (countryId && !isUSACountry) return ['addressLine1', 'city', 'countryId'];

    const fields: RequiredAddressField[] = ['addressLine1', 'city', 'state', 'zipCode'];
    if (isUSACountry) fields.push('countryId');
    return fields;
  }

  /**
   * Validate profile merge address has all required fields
   * @param value - Profile merge address model to validate
   * @throws Error if address is incomplete
   */
  static validateProfileMergeAddress(value: IProfileMergeRecordAddressModel): void {
    let isValid = false;

    const isUnitedStateCountry = !value.countryId || countryData.isUSA(value.countryId);
    if (isUnitedStateCountry) {
      isValid = !!(value.addressLine1 && value.city && value.state && value.zipCode);
    } else {
      isValid = !!(value.addressLine1 && value.city && value.countryId);
    }

    if (!isValid) throw new Error(UIErrorMessage.AddressFieldValidationMessage);
  }

  /**
   * Validate address form and populate errors object
   * Validates required fields based on country and updates form.errors
   * @param address - Address form model to validate (errors mutated in place)
   */
  static validateForm(address: AddressInputFormDataModel): void {
    const requiredFields = AddressValidator.requiredFields(address.countryId);

    const count = requiredFields.filter(
      (key: string) => address[key as keyof AddressInputFormDataModel]
    ).length;
    if (count === 0) return;

    requiredFields.forEach((key: string) => {
      const typeKey = key as keyof AddressInputFormDataModel;
      if (!address[typeKey]) {
        address.errors[typeKey] = UIErrorMessage.FieldRequired;
        return;
      }
      address.errors[typeKey] && delete address.errors[typeKey];
    });
  }
}
